import { useState, useEffect } from "react";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { Plus, Trash2, Users, Cog, FileText, Package, Ruler, Leaf } from "lucide-react";
import { LucideIcon } from "lucide-react";

interface IshikawaCategory {
  id: string;
  name: string;
  icon: LucideIcon;
  color: string;
  causes: string[];
}

interface IshikawaData {
  problem: string;
  categories: Array<{
    id: string;
    name: string;
    causes: string[];
  }>;
}

interface IshikawaDiagramProps {
  data: IshikawaData;
  onChange: (data: IshikawaData) => void;
}

const defaultCategories: Omit<IshikawaCategory, "causes">[] = [
  { id: "man", name: "Man (People)", icon: Users, color: "text-chart-2 bg-chart-2/10 border-chart-2/30" },
  { id: "machine", name: "Machine", icon: Cog, color: "text-chart-3 bg-chart-3/10 border-chart-3/30" },
  { id: "method", name: "Method", icon: FileText, color: "text-chart-1 bg-chart-1/10 border-chart-1/30" },
  { id: "material", name: "Material", icon: Package, color: "text-warning bg-warning/10 border-warning/30" },
  { id: "measurement", name: "Measurement", icon: Ruler, color: "text-success bg-success/10 border-success/30" },
  { id: "environment", name: "Environment", icon: Leaf, color: "text-chart-4 bg-chart-4/10 border-chart-4/30" },
];

export function IshikawaDiagram({ data, onChange }: IshikawaDiagramProps) {
  const [localData, setLocalData] = useState<IshikawaData>(data);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  useEffect(() => {
    // Initialize categories if empty
    if (!data.categories || data.categories.length === 0) {
      const initialData: IshikawaData = {
        problem: data.problem || "",
        categories: defaultCategories.map((cat) => ({ id: cat.id, name: cat.name, causes: [""] })),
      };
      setLocalData(initialData);
      onChange(initialData);
    } else {
      setLocalData(data);
    }
  }, [data]);

  const updateData = (updates: Partial<IshikawaData>) => {
    const newData = { ...localData, ...updates };
    setLocalData(newData);
    onChange(newData);
  };

  const updateCause = (categoryId: string, causeIndex: number, value: string) => {
    const newCategories = localData.categories.map((cat) => {
      if (cat.id === categoryId) {
        const newCauses = [...cat.causes];
        newCauses[causeIndex] = value;
        return { ...cat, causes: newCauses };
      }
      return cat;
    });
    updateData({ categories: newCategories });
  };

  const addCause = (categoryId: string) => {
    const newCategories = localData.categories.map((cat) => {
      if (cat.id === categoryId) {
        return { ...cat, causes: [...cat.causes, ""] };
      }
      return cat;
    });
    updateData({ categories: newCategories });
  };

  const removeCause = (categoryId: string, causeIndex: number) => {
    const newCategories = localData.categories.map((cat) => {
      if (cat.id === categoryId && cat.causes.length > 1) {
        return { ...cat, causes: cat.causes.filter((_, i) => i !== causeIndex) };
      }
      return cat;
    });
    updateData({ categories: newCategories });
  };

  const getCauseCount = (category: IshikawaData["categories"][0]) => {
    return category.causes.filter((c) => c.trim()).length;
  };

  const totalCauses = localData.categories?.reduce((sum, cat) => sum + getCauseCount(cat), 0) || 0;

  return (
    <div className="space-y-4">
      {/* Problem (Fish Head) */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">
          Effect / Problem
        </label>
        <Input
          placeholder="What is the effect or problem?"
          value={localData.problem}
          onChange={(e) => updateData({ problem: e.target.value })}
          className="bg-destructive/10 border-destructive/30 focus:border-destructive"
        />
      </div>

      {/* Visual Fishbone representation */}
      <div className="relative py-4">
        {/* Main spine */}
        <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary/50 via-primary to-primary/50" />
        
        {/* Categories (6M) */}
        <div className="grid grid-cols-2 gap-3">
          {localData.categories?.map((category, index) => {
            const IconComponent = defaultCategories.find((c) => c.id === category.id)?.icon || Users;
            const colorClass = defaultCategories.find((c) => c.id === category.id)?.color || "";
            const isLeft = index % 2 === 0;
            const isExpanded = expandedCategory === category.id;
            const causeCount = getCauseCount(category);

            return (
              <div
                key={category.id}
                className={`${isLeft ? "pr-4" : "pl-4"}`}
              >
                <button
                  onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                  className={`w-full p-3 rounded-lg border transition-all ${colorClass} ${
                    isExpanded ? "ring-2 ring-primary" : "hover:ring-1 hover:ring-primary/50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <IconComponent className="h-4 w-4 flex-shrink-0" />
                    <span className="font-medium text-sm truncate">{category.name}</span>
                    {causeCount > 0 && (
                      <span className="ml-auto text-xs bg-background/50 px-1.5 py-0.5 rounded">
                        {causeCount}
                      </span>
                    )}
                  </div>
                </button>

                {/* Expanded causes */}
                {isExpanded && (
                  <div className="mt-2 space-y-2 p-3 rounded-lg border border-border bg-card/50">
                    {category.causes.map((cause, causeIndex) => (
                      <div key={causeIndex} className="flex gap-2">
                        <Input
                          placeholder={`Add cause...`}
                          value={cause}
                          onChange={(e) => updateCause(category.id, causeIndex, e.target.value)}
                          className="flex-1 h-8 text-sm"
                        />
                        {category.causes.length > 1 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => removeCause(category.id, causeIndex)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full h-7 text-xs border-dashed"
                      onClick={() => addCause(category.id)}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Cause
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between pt-2 text-xs text-muted-foreground border-t border-border">
        <span>{totalCauses} potential causes identified</span>
        <span className="text-primary">Click a category to add causes</span>
      </div>
    </div>
  );
}
